import { AgGridReact } from '@mint-lib/ag-grid/react';
import { useCallback, useMemo, useRef, type RefObject } from 'react';

import {
  ClientSideRowModelApiModule,
  ColumnAutoSizeModule,
  DateFilterModule,
  ModuleRegistry,
  PaginationModule,
  PinnedRowModule,
  RenderApiModule,
  RowDragModule,
  RowSelectionModule,
  SelectionChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
  type ColDef,
  type GridApi,
  type ICellRendererParams,
  type IRowNode,
  type RowClassParams,
  type RowDragEvent,
} from '@mint-lib/ag-grid/community';
import {
  CellSelectionModule,
  ClientSideRowModelModule,
  ColumnMenuModule,
  ContextMenuModule,
  LicenseManager,
  RowGroupingModule,
  RowSelectionOptions,
  SetFilterModule,
} from '@mint-lib/ag-grid/enterprise';

import {
  asFileCellParams,
  dateFilterParams,
  FOLDER_DRAFT_ROW_ID,
  FOLDER_DRAFT_ROWS,
  foldersFirstComparator,
  isDraftRow,
} from '../../../../helpers/ag-grid-helpers.js';
import type { MoveVerdict } from '../../../../helpers/fileSystemMove.js';
import {
  getNodeRowId,
  isFolderNode,
} from '../../../../helpers/fileSystemNode.js';
import { getDocumentStatus } from '../../../../helpers/ragDocumentStatus.js';
import type {
  FileSystemNode,
  FolderNode,
} from '../../../../types/fileSystem.js';
import type { RagTableType } from '../../../../types/rag.js';
import {
  ActionsCell,
  DescriptionCell,
  FileTypeCell,
  StatusCell,
  UploadedDateCell,
  UserCell,
} from '../../RagTable/cells/index.js';
import { KnowledgeBaseNoRowOverlay } from '../../RagTable/NoRowsOverlay.jsx';
import { FolderActionsCell } from '../FolderSection/FolderActionsCell.jsx';
import { NameCell } from './cells/NameCell.jsx';

ModuleRegistry.registerModules([
  RowGroupingModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  RenderApiModule,
  PaginationModule,
  PinnedRowModule,
  ColumnAutoSizeModule,
  RowSelectionModule,
  RowDragModule,
  ContextMenuModule,
  ColumnMenuModule,
  SetFilterModule,
  DateFilterModule,
  CellSelectionModule,
]);

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

const FOLDER_CELL_PLACEHOLDER = '-';

// Applied through getRowClass. The grid owns the row DOM, so the highlight is
// repainted with a targeted redrawRows instead of by touching elements.
const DROP_TARGET_CLASS =
  'bg-background-info! outline-2 -outline-offset-2 outline-primary-main!';
const DROP_TARGET_INVALID_CLASS =
  'outline-2 -outline-offset-2 outline-error-main! opacity-60';

type DropTarget = { node: IRowNode<FileSystemNode>; isAllowed: boolean };

const getDraggedNodes = (event: RowDragEvent<FileSystemNode>) =>
  event.nodes
    .map((node) => node.data)
    .filter((data): data is FileSystemNode => Boolean(data));

type Props = {
  tableData: FileSystemNode[];
  isDrafting: boolean;
  onSelectNodes: (nodes: FileSystemNode[]) => void;
  onUnselectTableRows: () => void;
  onOpenDeleteDialog: (rows: RagTableType[]) => void;
  onOpenDeleteFolder: (folder: FolderNode) => void;
  onOpenEditPermissionsDialog: (document: RagTableType) => void;
  onOpenMoveDialog: (nodes: FileSystemNode[]) => void;
  onOpenFolder: (folder: FolderNode) => void;
  onStartRename: (id: string) => void;
  renamingFolderId: string | null;
  onCommitRename: (id: string, name: string) => boolean;
  onCancelRename: () => void;
  onCommitDraft: (name: string) => boolean;
  onCancelDraft: () => void;
  validateName: (name: string, excludeId?: string) => boolean;
  gridRef: RefObject<AgGridReact | null>;
  checkMove: (nodes: FileSystemNode[], targetFolderId: string) => MoveVerdict;
  onMoveNodes: (nodes: FileSystemNode[], targetFolderId: string) => void;
  onGridApiChange: (api: GridApi<FileSystemNode> | null) => void;
};

const FileSystemTable = ({
  tableData,
  isDrafting,
  onSelectNodes,
  onUnselectTableRows,
  onOpenMoveDialog,
  onOpenDeleteDialog,
  onOpenDeleteFolder,
  onOpenEditPermissionsDialog,
  onOpenFolder,
  onStartRename,
  renamingFolderId,
  onCommitRename,
  onCancelRename,
  onCommitDraft,
  onCancelDraft,
  validateName,
  gridRef,
  checkMove,
  onMoveNodes,
  onGridApiChange,
}: Props) => {
  // A ref, not state: the highlight changes on every row the pointer crosses
  // and re-rendering the grid mid-drag would tear down the dragged row.
  const dropTargetRef = useRef<DropTarget | null>(null);

  const paintDropTarget = useCallback(
    (api: GridApi<FileSystemNode>, next: DropTarget | null) => {
      const previous = dropTargetRef.current;

      if (
        previous?.node === next?.node &&
        previous?.isAllowed === next?.isAllowed
      ) {
        return;
      }

      dropTargetRef.current = next;

      const repainted = [previous?.node, next?.node].filter(
        (node): node is IRowNode<FileSystemNode> => Boolean(node),
      );

      if (repainted.length) api.redrawRows({ rowNodes: repainted });
    },
    [],
  );

  const getRowClass = useCallback((params: RowClassParams<FileSystemNode>) => {
    const target = dropTargetRef.current;

    if (target?.node !== params.node) return undefined;

    return target.isAllowed ? DROP_TARGET_CLASS : DROP_TARGET_INVALID_CLASS;
  }, []);

  const handleRowDragMove = useCallback(
    (event: RowDragEvent<FileSystemNode>) => {
      const overNode = event.overNode;

      if (!overNode || !isFolderNode(overNode.data)) {
        paintDropTarget(event.api, null);
        return;
      }

      const dragged = getDraggedNodes(event);

      paintDropTarget(event.api, {
        node: overNode,
        isAllowed: checkMove(dragged, overNode.data.id).allowed,
      });
    },
    [checkMove, paintDropTarget],
  );

  const handleRowDragEnd = useCallback(
    (event: RowDragEvent<FileSystemNode>) => {
      const target = dropTargetRef.current;

      paintDropTarget(event.api, null);

      if (!target?.isAllowed || !isFolderNode(target.node.data)) return;

      onMoveNodes(getDraggedNodes(event), target.node.data.id);
    },
    [onMoveNodes, paintDropTarget],
  );

  const handleRowDragLeave = useCallback(
    (event: RowDragEvent<FileSystemNode>) => paintDropTarget(event.api, null),
    [paintDropTarget],
  );

  const columnDefs = useMemo<ColDef<FileSystemNode>[]>(
    () => [
      {
        headerName: 'Name',
        valueGetter: (p: ValueGetterParams) =>
          isFolderNode(p.data) ? p.data.name : p.data?.fileName,
        rowDrag: ({ node, data }) =>
          !node.rowPinned && data?.id !== renamingFolderId,
        filter: true,
        comparator: foldersFirstComparator(),
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) => (
          <NameCell
            {...params}
            onOpenFolder={onOpenFolder}
            onCommitDraft={onCommitDraft}
            onCancelDraft={onCancelDraft}
            renamingFolderId={renamingFolderId}
            onCommitRename={onCommitRename}
            onCancelRename={onCancelRename}
            validateName={validateName}
          />
        ),
        suppressKeyboardEvent: (params: {
          node?: { rowPinned?: unknown; data?: FileSystemNode };
        }) =>
          Boolean(params.node?.rowPinned) ||
          params.node?.data?.id === renamingFolderId,
      },
      {
        headerName: 'Type',
        field: 'fileType',
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) =>
          isDraftRow(params) || isFolderNode(params.data)
            ? null
            : FileTypeCell(asFileCellParams(params)),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
        },
        filter: true,
        comparator: foldersFirstComparator(),
      },
      {
        headerName: 'Description',
        field: 'description',
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) => {
          if (isDraftRow(params)) return null;
          if (isFolderNode(params.data)) return FOLDER_CELL_PLACEHOLDER;

          return DescriptionCell(asFileCellParams(params));
        },
        comparator: foldersFirstComparator(),
      },
      {
        headerName: 'Uploaded date',
        field: 'uploadedDate',
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) => {
          if (isDraftRow(params)) return null;
          if (isFolderNode(params.data)) return FOLDER_CELL_PLACEHOLDER;

          return UploadedDateCell(asFileCellParams(params));
        },
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        comparator: foldersFirstComparator(),
      },
      {
        headerName: 'Uploaded by',
        colId: 'uploadedBy',
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) =>
          isDraftRow(params) || isFolderNode(params.data)
            ? null
            : UserCell(asFileCellParams(params)),
        valueGetter: (p: ValueGetterParams) =>
          isDraftRow(p) || isFolderNode(p.data)
            ? ''
            : `${p.data.firstName}_${p.data.lastName}`,
        filter: true,
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            if (!params.value) return '';

            const [firstName, lastName] = params.value.split('_');
            return `${firstName} ${lastName}`;
          },
        },
        comparator: foldersFirstComparator(),
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) =>
          isDraftRow(params) || isFolderNode(params.data)
            ? null
            : StatusCell(asFileCellParams(params)),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
        },
        filter: true,
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            const status = getDocumentStatus(params.value);

            if (!status) return '';

            return status.label.charAt(0).toUpperCase() + status.label.slice(1);
          },
        },
        comparator: foldersFirstComparator(),
      },
      {
        headerComponent: () => null,
        colId: 'actions',
        pinned: 'right' as boolean | 'left' | 'right' | null | undefined,
        filter: false,
        sortable: false,
        resizable: false,
        width: 40,
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) => {
          if (params.node?.rowPinned) return null;
          const node = params.data;

          if (!node) return null;

          if (isFolderNode(node)) {
            return (
              <FolderActionsCell
                onMove={() => onOpenMoveDialog([node])}
                onRename={() => onStartRename(node.id)}
                onDelete={() => onOpenDeleteFolder(node)}
                className="w-full"
              />
            );
          }

          return (
            <ActionsCell
              data={node}
              onOpenDeleteDialog={onOpenDeleteDialog}
              onUnselectTableRows={onUnselectTableRows}
              onOpenEditPermissionsDialog={onOpenEditPermissionsDialog}
              onOpenMoveDialog={() => onOpenMoveDialog([node])}
              className="w-full"
            />
          );
        },
        cellClass: '!p-0',
      },
    ],
    [
      onOpenMoveDialog,
      onOpenFolder,
      onCommitDraft,
      onCancelDraft,
      validateName,
      onStartRename,
      renamingFolderId,
      onCommitRename,
      onCancelRename,
      onOpenDeleteDialog,
      onOpenDeleteFolder,
      onUnselectTableRows,
      onOpenEditPermissionsDialog,
    ],
  );

  const rowSelection: RowSelectionOptions<FileSystemNode> = useMemo(() => {
    return {
      mode: 'multiRow' as 'multiRow' | 'singleRow',
      hideDisabledCheckboxes: true,
      //FYI: Row selection is used to delete files. We must reject row selection for data that has not yet been processed and put into context
      isRowSelectable: (rowNode) =>
        rowNode?.data?.kind === 'folder' || rowNode?.data?.status !== 'pending',
    };
  }, []);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);

      onSelectNodes(selectedData);
    },
    [onSelectNodes],
  );

  return (
    <div className="min-h-0 w-full flex-1" data-testid="file-system-table">
      <AgGridReact
        ref={gridRef}
        rowSelection={rowSelection}
        rowData={tableData}
        pinnedTopRowData={isDrafting ? FOLDER_DRAFT_ROWS : undefined}
        columnDefs={columnDefs}
        getRowId={(params) =>
          params.rowPinned ? FOLDER_DRAFT_ROW_ID : getNodeRowId(params.data)
        }
        rowModelType={'clientSide'}
        // Unmanaged on purpose: managed row drag reorders rows, and what we
        // want is a drop *onto* a row, which only the drag events expose.
        rowDragManaged={false}
        rowDragMultiRow
        getRowClass={getRowClass}
        onRowDragMove={handleRowDragMove}
        onRowDragEnd={handleRowDragEnd}
        onRowDragLeave={handleRowDragLeave}
        onRowDragCancel={handleRowDragLeave}
        onGridReady={({ api }) => onGridApiChange(api)}
        onGridPreDestroyed={() => onGridApiChange(null)}
        loadThemeGoogleFonts
        paginationAutoPageSize
        pagination
        autoSizeStrategy={{ type: 'fitGridWidth' }}
        onSelectionChanged={onSelectionChanged}
        suppressMovableColumns
        suppressContextMenu
        components={{
          agNoRowsOverlay: KnowledgeBaseNoRowOverlay,
        }}
        noRowsOverlayComponentParams={{ hidden: isDrafting }}
      />
    </div>
  );
};

export { FileSystemTable };
